import React, { useEffect, useState, useMemo } from 'react'
import Searchbar from '../../../pages/Connect/Searchbar'
import { getFileBaseUrl } from '../../../services/FilesService';
import { getGroupProfileImageUrl, getUserInitials, isValidImageUrl } from '../../../utils/general';
import { ImagePath } from '../../../assets/ImagePath';
import config from '../../../utils/config/config';
import { changeChannelOwner, fetchChannelParticipants, UserServiceClientApi } from '../../../services/api/UserApiServices';
import { useSelector } from 'react-redux';
import { getDataFromLFStorage, LFStorageKeys } from '../../../services/storage/LFStorage';
import { getAuthData } from '../../../services/storage/LocalStorage';
import { appType } from '../../../utils/config/configHelpers';
import TransferOwnerShipModal from './ModalMessages/TransferOwnerShipModal';
import webSocketApiService from '../../../services/socket/wsApiServices';

function EditGroupModal({ selectedContactDetails, editChannel, groupContacts, isChannelAdmin, isGroupOwner, channelAdmins }) {
    const [avatarMap, setAvatarMap] = useState({});
    const [channelImage, setChannelImage] = useState(null);
    const [searchMembers, setSearchMembers] = useState('');
    const [openOwerShipModal, setOpenOwerShipModal] = useState(false);
    const [selectedUserName, setSelectedUserName] = useState('');
    const [activeTab, setActiveTab] = useState('addMembers'); // Tab state
    const userDetail = getAuthData();
    const userId = userDetail.user?.userId;
    const selectedDomain = useSelector((state) => state.domain.selectedDomain);

    const myAddedContacts = groupContacts.contacts.filter((contact) => !contact?.waitingForConfirmation)

    const isUserAlreadyAdmin = (userId) => {
        return channelAdmins.some(admin => admin.userId === userId);
    };

    const closeOwerShipModal = () => {
        setOpenOwerShipModal(false)
    }

    const filteredMyAddedContacts = useMemo(() => {
        const cleanedContacts = myAddedContacts?.filter(contact => {

            if (contact.userId === selectedContactDetails?.channelOwner?.userId) return false;
            if (contact.userId === userId && isChannelAdmin) return false;
            return true;
        });

        if (!searchMembers.trim()) return cleanedContacts;

        return cleanedContacts.filter(contact => {
            const name = contact?.userName || '';
            return name.toLowerCase().includes(searchMembers.toLowerCase());
        });
    }, [myAddedContacts, searchMembers]);



    const [groupDetails, setGroupDetails] = useState({
        channelName: selectedContactDetails?.channelName || '',
        description: selectedContactDetails?.description || '',
        userId: selectedContactDetails?.userId || '',
        participants: selectedContactDetails?.participants || [],
        imgFileName: '',
        imgFile: '',
        chatOff: selectedContactDetails?.chatOff || false
    });

    // Separate original contacts from filtered contacts
    const [originalGroupContacts, setOriginalGroupContacts] = useState([]);
    const [admins, setAdmins] = useState([]); // Track admins
    const [owner, setOwner] = useState(selectedContactDetails?.userId || userId); // Track owner

    // Tab configuration
    const tabs = [
        { id: 'addMembers', label: 'Add Members' },
        { id: 'manageAdmin', label: 'Manage Admin' },
        { id: 'transferOwnership', label: 'Transfer Ownership' }
    ];

    // Memoized filtered contacts based on search
    const filteredGroupContacts = useMemo(() => {
        if (!searchMembers.trim()) {
            return originalGroupContacts;
        }

        return originalGroupContacts.filter(contact => {
            const name = contact?.userName || '';
            return name.toLowerCase().includes(searchMembers.toLowerCase());
        });
    }, [originalGroupContacts, searchMembers]);

    useEffect(() => {
        if (selectedContactDetails) {
            const loadParticipants = async () => {
                try {
                    let payload = {
                        channelName: selectedContactDetails?.channelName,
                        userDomain: selectedDomain
                    }
                    const participantsData = await webSocketApiService.GetParticipants(payload);
                    console.log('participantsData', participantsData);
                } catch (error) {
                    console.error('Error fetching participants:', error);
                }
            };

            loadParticipants();
        }
    }, [selectedContactDetails]);

    const getGroupParticipants = async (channelName) => {
        try {
            const params = {
                channelName: channelName,
                appType,
                platform: "web",
                userDomain: selectedDomain
            }
            const response = await webSocketApiService.GetParticipants(params);

            if (response.status === 200) {
                let participants = Array.isArray(response?.content) ? response.content : [];
                participants.push(userDetail.user);

                const combined = [...participants];

                const contactMap = {};
                for (const p of [...participants, ...groupContacts.contacts]) {
                    contactMap[p.userId] = p;
                }

                const mergedList = Object.values(contactMap);

                setOriginalGroupContacts(mergedList);
            }

        } catch (err) {
            console.error("Error fetching group participants:", err);
        }
    };

    useEffect(() => {
        const loadAvatars = async () => {
            if (!selectedContactDetails) return;

            const avatars = {};

            // Load participant avatars
            const participants = selectedContactDetails.participants || [];

            await Promise.all(
                participants.map(async (participantId) => {
                    const filename = `${participantId}_75x75.png`;

                    try {
                        const imageUrl = await getGroupProfileImageUrl(filename);
                        const resolvedUrl = await isValidImageUrl(imageUrl, participantId);

                        if (resolvedUrl !== ImagePath.dummyUser) {
                            avatars[participantId] = resolvedUrl;
                        }
                        // else fallback to initials if needed
                    } catch (err) {
                        console.error(`Error loading avatar for participant ${participantId}:`, err);
                    }
                })
            );

            // Load channel avatar
            const channelId = selectedContactDetails.channelId;
            if (selectedContactDetails && selectedContactDetails?.channelId) {
                if (selectedContactDetails.logo == 'ChannelsBotLogo.png') {
                    const imageUrl = await getGroupProfileImageUrl(selectedContactDetails?.channelId);
                    avatars[channelId] = imageUrl;
                } else {
                    const profileUtils = getUserInitials(selectedContactDetails?.channelName);
                    avatars[channelId] = profileUtils;
                }

                setAvatarMap(avatars);
            }
        };

        loadAvatars();
        getGroupParticipants(selectedContactDetails?.channelName);
        setGroupDetails((prevData) => ({ ...prevData, participants: selectedContactDetails?.participants || [] }));
    }, [selectedContactDetails]);

    const handleEditChannel = () => {
        editChannel({
            ...groupDetails,
            admins,
            owner,
            channelId: selectedContactDetails.channelId,

        });
    };

    const toggleMember = (userId) => {
        setGroupDetails(prev => {
            const currentParticipants = prev.participants || [];

            const updatedParticipants = currentParticipants.includes(userId)
                ? currentParticipants.filter(id => id !== userId)
                : [...currentParticipants, userId];

            return {
                ...prev,
                participants: updatedParticipants
            };
        });
    };

    const toggleAdmin = (userId) => {

        const currentAdminIds = channelAdmins.map(admin => admin.userId);

        const isAlreadyAdmin = currentAdminIds.includes(userId);
        const updatedAdminIds = isAlreadyAdmin
            ? currentAdminIds.filter(id => id !== userId)
            : [...currentAdminIds, userId];
        setAdmins(updatedAdminIds);
        const payload = {
            channelName: groupDetails?.channelName,
            userDomain: selectedDomain,
            admins: updatedAdminIds,
        };

        webSocketApiService.UpdateChannelAdmins(payload);
    };


    const transferOwnership = (newOwnerId) => {
        setOwner(newOwnerId);
        if (!admins.includes(owner)) {
            setAdmins(prev => [...prev, owner]);
        }
        let obj = {
            channelName: groupDetails?.channelName,
            userDomain: selectedDomain,
            newOwnerId: newOwnerId,
        };

        changeChannelOwner(obj);
        closeOwerShipModal()
    };

    const channelImgUpload = (fileName, file) => {
        setGroupDetails((prevData) => ({ ...prevData, imgFileName: fileName, imgFile: file }));
    };

    const uploadPhoto = (e) => {
        let filesObj = e.target.files;
        if (!filesObj) {
            return;
        }

        let re = /(?:\.([^.]+))?$/;
        let file = filesObj[0];
        let fileName = file.name;
        let fileExtension = re.exec(fileName)[1];
        let { channelId } = selectedContactDetails;

        if (channelId) {
            channelImgUpload(channelId + "." + fileExtension, file);
        } else {
            channelImgUpload(fileExtension, file);
        }

        setGroupDetails((prevData) => ({ ...prevData, imgFile: file, updatedFile: URL.createObjectURL(file) }));
    };

    const filteredParticipants = groupDetails.participants?.filter(participantId => {
        if (participantId === selectedContactDetails?.channelOwner?.userId) return false;

        if (participantId === userId && isChannelAdmin) return false;

        return true;
    });


    const toggleOff = () => {
        setGroupDetails((prev) => ({ ...prev, chatOff: !prev?.chatOff }));

    }
    // Render tab content based on active tab
    const renderTabContent = () => {
        const savedParticipants = selectedContactDetails?.participants || [];

        const participantContacts = (filteredGroupContacts || [])
            .filter(contact => {
                const isOwner = contact.userId === selectedContactDetails?.channelOwner?.userId;
                const isLoggedInAdmin = contact.userId === userId && isChannelAdmin;

                return savedParticipants.includes(contact?.userId) && !isOwner && !isLoggedInAdmin;
            });

        const nonParticipantContacts = filteredGroupContacts?.filter(contact =>
            !groupDetails?.participants?.includes(contact?.userId)
        ) || [];

        switch (activeTab) {
            case 'addMembers':
                return (
                    <div className="flex flex-col gap-[20px]  pb-[10px]">
                        <div className="flex justify-between items-center">
                            <p className="font-semibold text-[#15194B] text-[14px]">
                                All Contacts ({filteredMyAddedContacts.length})
                            </p>
                            {isGroupOwner && (
                                 <div className="flex items-center space-x-2">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={groupDetails?.chatOff}
                                        onChange={() => toggleOff()}
                                    />

                                    {/* Track */}
                                    <div className="w-[44px] h-[24px] bg-[#D2D5DA] rounded-full peer-checked:bg-[#15194B] transition-colors" />

                                    {/* Knob */}
                                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5" />
                                </label>

                                <span className="text-[14px] font-semibold">
                                    {groupDetails?.chatOff ? "Off" : "On"}
                                </span>
                            </div> 
                            )}
                        </div>

                        {filteredMyAddedContacts.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                {searchMembers ? 'No contacts found matching your search.' : 'No available contacts.'}
                            </div>
                        ) : (
                            filteredMyAddedContacts.map((contact) => (
                                <ContactItem
                                    key={contact.userId}
                                    contact={contact}
                                    avatarMap={avatarMap}
                                    userId={userId}
                                    isChecked={groupDetails?.participants?.includes(contact?.userId)}
                                    onToggle={() => toggleMember(contact?.userId)}
                                />
                            ))
                        )}
                    </div>
                );

            case 'manageAdmin':
                return (
                    <div className="flex flex-col gap-[20px]   pb-[10px]">
                        <div className="flex justify-between items-center">
                            <p className="font-semibold text-[#15194B] text-[14px]">
                                Group Members ({participantContacts.length})
                            </p>
                        </div>
                        {participantContacts.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                {searchMembers ? 'No group members found matching your search.' : 'No group members.'}
                            </div>
                        ) : (
                            participantContacts.map((contact) => (
                                <AdminContactItem
                                    key={contact.userId}
                                    contact={contact}
                                    avatarMap={avatarMap}
                                    userId={userId}
                                    isAdmin={isChannelAdmin}
                                    isOwner={isGroupOwner}
                                    onToggleAdmin={() => toggleAdmin(contact?.userId)}
                                    isUserAlreadyAdmin={isUserAlreadyAdmin}
                                    selectedContactDetails={selectedContactDetails}
                                />
                            ))
                        )}
                    </div>
                );

            case 'transferOwnership':
                const transferCandidates = participantContacts.filter(contact => contact.userId !== owner);
                return (
                    <div className="flex flex-col gap-[20px]  pb-[10px]">
                        <div className="flex justify-between items-center">
                            <p className="font-semibold text-[#15194B] text-[14px]">
                                Select New Owner ({transferCandidates.length})
                            </p>
                        </div>
                        {transferCandidates.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                {searchMembers ? 'No eligible members found matching your search.' : 'No eligible members for ownership transfer.'}
                            </div>
                        ) : (
                            transferCandidates.map((contact) => (
                                <OwnershipContactItem
                                    key={contact.userId}
                                    contact={contact}
                                    avatarMap={avatarMap}
                                    userId={userId}
                                    isSelected={false}
                                    onSelect={() => transferOwnership(contact?.userId)}
                                    closeOwerShipModal={closeOwerShipModal}
                                    openOwerShipModal={openOwerShipModal}
                                    setOpenOwerShipModal={setOpenOwerShipModal}
                                    setSelectedUserName={setSelectedUserName}
                                    selectedUserName={selectedUserName}
                                    selectedContactDetails={selectedContactDetails}
                                />
                            ))
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <>
            <div>
                <h2 className="text-[16px] font-bold text-[#404585] text-center mb-[10px] font-figtree">Edit Group</h2>
                <p className="text-[#727484] text-[14px] font-normal text-center">Only Group description, photo and participants can be modified.</p>
            </div>
            <div className="flex bg-white rounded-lg p-6 mt-[30px] h-[60vh] overflow-auto">
                <div className="w-1/2 pr-[50px]">
                    <div className="flex flex-col items-center mb-[20px]">
                        <div className="relative w-[95px] h-[95px] bg-[#727484] rounded-full">
                            {/* Conditional Rendering for Group Image or Avatar */}
                            {groupDetails.updatedFile ? (
                                <img
                                    src={groupDetails.updatedFile}
                                    className="rounded-full w-[95px] h-[95px]"
                                    alt="Group"
                                />
                            ) : (
                                typeof avatarMap[selectedContactDetails?.channelId] === 'string' &&
                                    avatarMap[selectedContactDetails?.channelId].startsWith('http') ? (
                                    <img
                                        src={avatarMap[selectedContactDetails?.channelId]}
                                        className="rounded-full w-[95px] h-[95px]"
                                        alt="Group"
                                    />
                                ) : (
                                    <div className="bg-[rgb(201,197,197)] rounded-full w-[95px] h-[95px] flex items-center justify-center text-[28px] font-medium text-white">
                                        {avatarMap[selectedContactDetails?.channelId]}
                                    </div>
                                )
                            )}

                            {/* Upload Button */}
                            <button
                                className="absolute bottom-[7px] right-0 bg-[#00ADFE] rounded-full w-[21px] h-[21px] flex justify-center items-center"
                                aria-label="Upload Image"
                                onClick={() => document.getElementById('localContactImageBrowser').click()}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
                                    <path d="M13.2429 2.08561H11.4928L11.2264 1.46107C10.9716 0.743314 10.2934 0.261719 9.53219 0.261719L6.23552 0.262496C5.47427 0.262496 4.79379 0.744111 4.54134 1.46185L4.27491 2.08639H2.47741C1.52663 2.08639 0.75293 2.86006 0.75293 3.81087V10.2598C0.75293 11.2106 1.52661 11.9843 2.47741 11.9843H13.2438C14.1946 11.9843 14.9683 11.2106 14.9683 10.2598L14.9675 3.81007C14.9675 2.85929 14.1937 2.08561 13.2429 2.08561ZM3.00787 4.81216C2.69948 4.81216 2.4509 4.56358 2.4509 4.2552C2.4509 3.94681 2.69948 3.69823 3.00787 3.69823C3.31626 3.69823 3.56483 3.94681 3.56483 4.2552C3.56483 4.56358 3.31626 4.81216 3.00787 4.81216ZM7.86041 9.6647C6.3845 9.6647 5.18974 8.46923 5.18974 6.99403C5.18974 5.51813 6.38522 4.32336 7.86041 4.32336C9.33632 4.32336 10.5311 5.51884 10.5311 6.99403C10.5311 8.46917 9.3356 9.6647 7.86041 9.6647Z" fill="white" />
                                </svg>
                            </button>

                            {/* Hidden Input for File Upload */}
                            <input
                                type="file"
                                id="localContactImageBrowser"
                                name="customFile"
                                accept=".png"
                                onChange={uploadPhoto}
                                className="hidden"
                            />
                        </div>
                    </div>

                    <div className="mb-4 w-full">
                        <div className="flex justify-space border-b border-[rgba(50,71,92,0.10)] mb-[8px]">
                            <input
                                type="text"
                                placeholder="Enter group name"
                                value={groupDetails.channelName}
                                onChange={(e) => setGroupDetails({ ...groupDetails, channelName: e.target.value })}
                                className="w-full pl-[4px] py-[8px] text-[#8387BD] text-[14px] font-normal focus:outline-none"
                                maxLength="50"
                                readOnly
                            />
                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                                <path d="M15 3.75C8.79436 3.75 3.75 8.79436 3.75 15C3.75 21.2056 8.79436 26.25 15 26.25C21.2056 26.25 26.25 21.2056 26.25 15C26.25 8.79436 21.2056 3.75 15 3.75ZM10.725 10.1153C11.5887 10.1153 12.2855 10.8121 12.2855 11.6758C12.2855 12.5322 11.5887 13.2363 10.725 13.2363C9.86128 13.2363 9.16454 12.5322 9.16454 11.6758C9.16453 10.8121 9.86128 10.1153 10.725 10.1153ZM10.1298 17.4097C10.5508 16.9887 11.2403 16.9887 11.6685 17.4097C12.5613 18.3024 13.7444 18.796 15 18.796C16.2556 18.796 17.4387 18.3024 18.3315 17.4097C18.7597 16.9887 19.4492 16.9887 19.8702 17.4097C20.2984 17.8379 20.2984 18.5274 19.8702 18.9484C18.5637 20.2548 16.8363 20.9734 15 20.9734C13.1637 20.9734 11.4363 20.2548 10.1298 18.9484C9.70162 18.5274 9.70162 17.8379 10.1298 17.4097ZM19.275 13.2363C18.4113 13.2363 17.7145 12.5322 17.7145 11.6758C17.7145 10.8121 18.4113 10.1153 19.275 10.1153C20.1387 10.1153 20.8355 10.8121 20.8355 11.6758C20.8355 12.5322 20.1387 13.2363 19.275 13.2363Z" fill="#638DFF" />
                            </svg>
                        </div>
                        <p className="text-[13px] font-normal tracking-[-0.395px] text-[#8387BD]">{groupDetails.channelName.length}/50 Characters</p>
                    </div>

                    <div className="mb-4 w-full mb-[70px]">
                        <textarea
                            placeholder="Enter group description"
                            className="w-full border border-[#F3F3FA] rounded p-[12px] h-[140px] resize-none bg-[#F3F3FA] rounded-[8px] focus:outline-none"
                            maxLength="140"
                            value={groupDetails.description}
                            onChange={(e) => setGroupDetails({ ...groupDetails, description: e.target.value })}
                        ></textarea>
                        <p className="text-[13px] font-normal tracking-[-0.395px] text-[#8387BD]">{groupDetails.description.length}/140 Characters</p>
                    </div>

                    <div className="flex gap-[15px]">
                        <button className="border border-[#15194B] text-[#15194B] text-[13px] px-[35px] py-[15px] rounded-[7px] w-[175px]">Cancel</button>
                        <button className="text-[#fff] text-[13px] px-[35px] py-[15px] rounded-[7px] w-[175px] bg-[#404585] hover:bg-[#333461]" onClick={handleEditChannel}>Edit Group</button>
                    </div>
                </div>

                <div className="w-1/2 flex flex-col gap-[25px] pl-[50px]">
                    <Searchbar setSearchMembers={setSearchMembers} searchMembers={searchMembers} />

                    {/* Selected Members Preview */}
                    <div className='flex gap-5 whitespace-nowrap pt-2'>
                        {filteredParticipants?.slice(0, 5).map((participantId) => {
                            const contact = originalGroupContacts?.find(c => c.userId === participantId);
                            return (
                                <div key={participantId} className='block'>
                                    <div className='userImage relative h-[42px] w-[42px] rounded-full'>
                                        {typeof avatarMap[participantId] === 'string' && avatarMap[participantId]?.startsWith("http") ? (
                                            <img src={avatarMap[participantId]} className='h-full w-full rounded-full' alt={contact?.userName} />
                                        ) : (
                                            <div className="bg-primary-blue rounded-full h-[42px] w-[42px] flex items-center justify-center text-white text-sm">
                                                {avatarMap[participantId] || contact?.userName?.charAt(0)}
                                            </div>
                                        )}
                                        <button
                                            className='absolute top-[-3px] right-[-2px]'
                                            onClick={() => toggleMember(participantId)}
                                        >
                                            <img src={ImagePath.xcircle} alt="Remove" />
                                        </button>
                                    </div>
                                    <span className='text-center text-primary-blue text-[14px] mx-auto block truncate w-[42px]'>
                                        {contact?.userName || 'User'}
                                    </span>
                                </div>
                            );
                        })}
                        {filteredParticipants?.length > 5 && (
                            <div className='flex items-center justify-center'>
                                <span className='text-[#8387BD] text-[12px]'>+{filteredParticipants?.length - 5} more</span>
                            </div>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className='grid grid-cols-3'>
                        {tabs
                            .filter(tab => tab.id !== 'transferOwnership' || isGroupOwner)
                            .map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`text-[14px] font-medium text-center pb-[10px] transition-colors duration-200 ${activeTab === tab.id
                                        ? 'border-b-2 border-b-[#2563EB] text-[#2563EB]'
                                        : 'text-[#6D7280] hover:text-[#2563EB] hover:border-b-2 hover:border-b-[#2563EB]'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                    </div>

                    {/* Tab Content */}
                    {renderTabContent()}
                </div>
            </div>
        </>
    );
}

// Contact Item Component for Add Members tab
const ContactItem = ({ contact, avatarMap, userId, isChecked, onToggle }) => (
    <div className="flex items-center justify-between py-[4px]">
        <div className="flex items-center gap-[8px]">
            <div className="relative w-[45px] h-[45px] bg-[#727484] rounded-full">
                {typeof avatarMap[contact?.userId] === 'string' && avatarMap[contact?.userId]?.startsWith("http") ? (
                    <img
                        src={avatarMap[contact?.userId]}
                        alt={contact?.userName}
                        className="w-[45px] h-[45px] rounded-full object-cover border border-gray-300"
                    />
                ) : (
                    <div className="rounded-full bg-primary-blue w-[45px] h-[45px] bg-gray-500 flex items-center justify-center text-white text-sm font-[Figtree]">
                        {avatarMap[contact?.userId] || contact?.userName?.charAt(0)}
                    </div>
                )}
            </div>
            <div>
                <p className="font-semibold text-[14px] text-[#15194B]">
                    {contact?.userName} {contact?.userId === userId && "(You)"}
                </p>
                <div className="flex space-x-2 text-xs items-center">
                    {contact?.role &&
                        <span className="bg-[#376AFA] text-[13px] text-[#fff] rounded-[14px] py-[4px] px-[8px]">
                            {contact?.role}
                        </span>}
                    <span className="text-[#404585] font-normal tracking-[-0.395px] text-[13px]">
                        {contact?.rank}
                    </span>
                </div>
            </div>
        </div>
        <input
            type="checkbox"
            className="form-checkbox w-[22px] h-[22px] text-[#8387BD] accent-[#404585] rounded-[6px]"
            checked={isChecked}
            onChange={onToggle}
        />
    </div>
);

// Admin Contact Item Component for Manage Admin tab
const AdminContactItem = ({ contact, avatarMap, userId, isAdmin, isOwner, onToggleAdmin, isUserAlreadyAdmin, selectedContactDetails }) => (
    <div className="flex items-center justify-between py-[4px]">
        <div className="flex items-center gap-[8px]">
            <div className="relative w-[45px] h-[45px] bg-[#727484] rounded-full">
                {typeof avatarMap[contact?.userId] === 'string' && avatarMap[contact?.userId]?.startsWith("http") ? (
                    <img
                        src={avatarMap[contact?.userId]}
                        alt={contact?.userName}
                        className="w-[45px] h-[45px] rounded-full object-cover border border-gray-300"
                    />
                ) : (
                    <div className="rounded-full bg-primary-blue w-[45px] h-[45px] bg-gray-500 flex items-center justify-center text-white text-sm font-[Figtree]">
                        {avatarMap[contact?.userId] || contact?.userName?.charAt(0)}
                    </div>
                )}
                {selectedContactDetails?.channelOwner.userId === contact?.userId && (
                    <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full w-4 h-4 flex items-center justify-center">
                        <span className="text-white text-xs">👑</span>
                    </div>
                )}
            </div>
            <div>
                <p className="font-semibold text-[14px] text-[#15194B]">
                    {contact?.userName} {contact?.userId === userId && "(You)"}
                    {selectedContactDetails?.channelOwner.userId === contact?.userId && <span className="text-yellow-600 ml-2">(Owner)</span>}
                </p>
                <div className="flex space-x-2 text-xs items-center">
                    {contact?.role &&
                        <span className={`text-[13px] text-[#fff] rounded-[14px] py-[4px] px-[8px] ${isAdmin ? 'bg-green' : 'bg-[#376AFA]'
                            }`}>
                            {contact?.role}
                        </span>}
                    <span className="text-[#404585] font-normal tracking-[-0.395px] text-[13px]">
                        {contact?.rank}
                    </span>
                </div>
            </div>
        </div>
        {(isOwner || isAdmin) && (
            <button
                onClick={onToggleAdmin}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${isUserAlreadyAdmin(contact?.userId)
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
            >
                {isUserAlreadyAdmin(contact?.userId) ? 'Remove Admin' : 'Make Admin'}
            </button>
        )}
    </div>
);

// Ownership Contact Item Component for Transfer Ownership tab
const OwnershipContactItem = ({ contact, avatarMap, userId, onSelect, closeOwerShipModal, openOwerShipModal, setOpenOwerShipModal, selectedUserName, setSelectedUserName }) => (
    <div className="flex items-center justify-between py-[4px] hover:bg-gray-50 rounded-lg px-2 transition-colors">
        <div className="flex items-center gap-[8px]">
            <div className="relative w-[45px] h-[45px] bg-[#727484] rounded-full">
                {typeof avatarMap[contact?.userId] === 'string' && avatarMap[contact?.userId]?.startsWith("http") ? (
                    <img
                        src={avatarMap[contact?.userId]}
                        alt={contact?.userName}
                        className="w-[45px] h-[45px] rounded-full object-cover border border-gray-300"
                    />
                ) : (
                    <div className="rounded-full bg-primary-blue w-[45px] h-[45px] bg-gray-500 flex items-center justify-center text-white text-sm font-[Figtree]">
                        {avatarMap[contact?.userId] || contact?.userName?.charAt(0)}
                    </div>
                )}
            </div>
            <div>
                <p className="font-semibold text-[14px] text-[#15194B]">
                    {contact?.userName} {contact?.userId === userId && "(You)"}
                </p>
                <div className="flex space-x-2 text-xs items-center">
                    {contact?.role &&
                        <span className="bg-[#376AFA] text-[13px] text-[#fff] rounded-[14px] py-[4px] px-[8px]">
                            {contact?.role}
                        </span>}
                    <span className="text-[#404585] font-normal tracking-[-0.395px] text-[13px]">
                        {contact?.rank}
                    </span>
                </div>
            </div>
        </div>
        <button
            // onClick={onSelect}
            onClick={() => {
                setSelectedUserName(contact?.userName);
                setOpenOwerShipModal(true);
            }}

            className="bg-slate-600 hover:bg-slate-600 text-white px-4 py-2 text-xs rounded-full transition-colors"
        >
            Transfer Ownership
        </button>
        <TransferOwnerShipModal isOpen={openOwerShipModal} onClose={closeOwerShipModal} onConfirm={onSelect} contactName={selectedUserName} />
    </div>
);


export default EditGroupModal;